import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import ContactQualificationAiQualification from '../enums/contact/contact_qualification_ai/ContactQualificationAiQualification';
import { ScenarioSample } from './scenario/ScenarioSample';
import ScenarioBusinessPriority, {
  scenarioBusinessPriorityAsEnum,
  scenarioBusinessPriorityAsNumber,
} from '../enums/scenario/ScenarioBusinessPriority';
import { v4 as uuidv4 } from 'uuid';

export default class Scenario {
  private _id: string;
  private _idDomain: string;
  private _name: string;
  private _nameShort: string;
  private _businessPriority: ScenarioBusinessPriority;
  private _description: string;
  private _constraints: string;
  private _criteria: string[];
  private _target: string;
  private _behavior: string;
  private _escalationRules: ContactQualificationAiQualification[];
  private _escalationNeedsQualification: boolean;
  private _fidelityModelFirstMessage: boolean;
  private _qualificationInstructions: string;
  private _samples: ScenarioSample[];
  private _created: Date;
  private _updated: Date;
  private _link_calendar_booking: string;

  constructor(
    id: string,
    idDomain: string,
    name: string,
    nameShort: string,
    businessPriority: ScenarioBusinessPriority,
    description: string,
    constraints: string,
    criteria: string[],
    target: string,
    behavior: string,
    escalationRules: ContactQualificationAiQualification[],
    escalationNeedsQualification: boolean,
    fidelityModelFirstMessage: boolean,
    qualificationInstructions: string,
    samples: ScenarioSample[],
    created: Date,
    updated: Date,
    link_calendar_booking = ''
  ) {
    this._id = id;
    this._idDomain = idDomain;
    this._name = name;
    this._nameShort = nameShort;
    this._businessPriority = businessPriority;
    this._description = description;
    this._constraints = constraints;
    this._criteria = criteria;
    this._target = target;
    this._behavior = behavior;
    this._escalationRules = escalationRules;
    this._escalationNeedsQualification = escalationNeedsQualification;
    this._fidelityModelFirstMessage = fidelityModelFirstMessage;
    this._qualificationInstructions = qualificationInstructions;
    this._samples = samples;
    this._created = created;
    this._updated = updated;
    this._link_calendar_booking = link_calendar_booking;
  }

  get id(): string {
    return this._id;
  }

  get idDomain(): string {
    return this._idDomain;
  }

  get name(): string {
    return this._name;
  }

  get nameShort(): string {
    return this._nameShort;
  }

  get businessPriority(): ScenarioBusinessPriority {
    return this._businessPriority;
  }

  get description(): string {
    return this._description;
  }

  get constraints(): string {
    return this._constraints;
  }

  get criteria(): string[] {
    return this._criteria;
  }

  get target(): string {
    return this._target;
  }

  get behavior(): string {
    return this._behavior;
  }

  get escalationRules(): ContactQualificationAiQualification[] {
    return this._escalationRules;
  }

  get escalationNeedsQualification(): boolean {
    return this._escalationNeedsQualification;
  }

  get fidelityModelFirstMessage(): boolean {
    return this._fidelityModelFirstMessage;
  }

  get qualificationInstructions(): string {
    return this._qualificationInstructions;
  }

  get samples(): ScenarioSample[] {
    return this._samples;
  }

  get created(): Date {
    return this._created;
  }

  get updated(): Date {
    return this._updated;
  }

  get link_calendar_booking(): string {
    return this._link_calendar_booking;
  }

  public copyWith({
    idDomain,
    name,
    nameShort,
    businessPriority,
    description,
    constraints,
    criteria,
    target,
    behavior,
    escalationRules,
    escalationNeedsQualification,
    fidelityModelFirstMessage,
    qualificationInstructions,
    samples,
    created,
    updated,
    link_calendar_booking,
  }: {
    idDomain?: string;
    name?: string;
    nameShort?: string;
    businessPriority?: ScenarioBusinessPriority;
    description?: string;
    constraints?: string;
    criteria?: string[];
    target?: string;
    behavior?: string;
    escalationRules?: ContactQualificationAiQualification[];
    escalationNeedsQualification?: boolean;
    fidelityModelFirstMessage?: boolean;
    qualificationInstructions?: string;
    samples?: ScenarioSample[];
    created?: Date;
    updated?: Date;
    link_calendar_booking?: string;
  }): Scenario {
    return new Scenario(
      this._id,
      idDomain ?? this._idDomain,
      name ?? this._name,
      nameShort ?? this._nameShort,
      businessPriority ?? this._businessPriority,
      description ?? this._description,
      constraints ?? this._constraints,
      criteria ?? this._criteria,
      target ?? this._target,
      behavior ?? this._behavior,
      escalationRules ?? this._escalationRules,
      escalationNeedsQualification ?? this._escalationNeedsQualification,
      fidelityModelFirstMessage ?? this._fidelityModelFirstMessage,
      qualificationInstructions ?? this._qualificationInstructions,
      samples ?? this._samples,
      created ?? this._created,
      updated ?? this._updated,
      link_calendar_booking ?? this._link_calendar_booking
    );
  }

  static generateScenarioId(): string {
    const uuidBase = uuidv4().toUpperCase();

    return (
      'scenario-' + uuidBase.substring(uuidBase.length - 10, uuidBase.length)
    );
  }

  static newWithDomainId(domainId: string): Scenario {
    return new Scenario(
      '',
      domainId,
      '',
      '',
      ScenarioBusinessPriority.one,
      '',
      '',
      [],
      '',
      '',
      [],
      false,
      true,
      '',
      [],
      new Date(),
      new Date(),
      ''
    );
  }

  static fromFirestore(
    doc: QueryDocumentSnapshot<DocumentData, DocumentData>
  ): Scenario {
    const docData = doc.data();

    const escalationRules = (docData.escalation_rules ?? []).map(
      (rule: string) => rule as ContactQualificationAiQualification
    );

    const samples = (docData.samples ?? []).map((sample: any) =>
      ScenarioSample.fromFirestoreMap(sample)
    );

    return new Scenario(
      doc.id,
      docData.id_domain,
      docData.name,
      docData.name_short,
      scenarioBusinessPriorityAsEnum(docData.business_priority),
      docData.description,
      docData.constraints,
      docData.criteria ?? [],
      docData.target,
      docData.behavior || '',
      escalationRules,
      docData.escalation_needs_qualification ?? false,
      docData.fidelity_model_first_message ?? true,
      docData.qualif_instructions ?? '',
      samples,
      new Date(docData.created),
      new Date(docData.updated),
      docData.link_calendar_booking || ''
    );
  }

  toFirestore(): Record<string, any> {
    return {
      id: this._id,
      id_domain: this._idDomain,
      name: this._name,
      name_short: this._nameShort,
      business_priority: scenarioBusinessPriorityAsNumber(
        this._businessPriority
      ),
      description: this._description,
      constraints: this._constraints,
      criteria: this._criteria,
      target: this._target,
      behavior: this._behavior,
      escalation_rules: this._escalationRules,
      escalation_needs_qualification: this._escalationNeedsQualification,
      fidelity_model_first_message: this._fidelityModelFirstMessage,
      qualif_instructions: this._qualificationInstructions,
      samples: this._samples.map((sample) => sample.toFirestoreMapPatch()),
      created: this._created.getTime(),
      updated: this._updated.getTime(),
      link_calendar_booking: this._link_calendar_booking,
    };
  }

  toFirestorePatch(): Record<string, any> {
    return {
      id_domain: this._idDomain,
      name: this._name,
      name_short: this._nameShort,
      business_priority: scenarioBusinessPriorityAsNumber(
        this._businessPriority
      ),
      description: this._description,
      constraints: this._constraints,
      criteria: this._criteria,
      target: this._target,
      behavior: this._behavior,
      escalation_rules: this._escalationRules,
      escalation_needs_qualification: this._escalationNeedsQualification,
      fidelity_model_first_message: this._fidelityModelFirstMessage,
      qualif_instructions: this._qualificationInstructions,
      samples: this._samples.map((sample) => sample.toFirestoreMapPatch()),
      updated: new Date().getTime(),
      link_calendar_booking: this._link_calendar_booking,
    };
  }
}
